/**
 * Awards countdown: keeps every [data-awards-countdown] fresh and fires
 * `cf-awards:countdown-end` on document once its target passes
 * (detail.end: the target as a timestamp in ms, once per target).
 *
 * Markup: template-parts/awards/partials/countdown.php, variants `inline`
 * ("53 dagar 10 tim") and `large` (48 · 07 · 53). One shared one-second timer;
 * text is written only when it changes (minute precision). The target can be
 * moved later with setCountdownEnd(), e.g. by the hero when the state changes.
 */

export const END_EVENT = 'cf-awards:countdown-end';

const MINUTE = 60;
const HOUR = 60;
const DAY = 24 * 60;

const roots = new Set();
const fired = new Set();

/**
 * Split seconds left into whole days, hours and minutes, rounding up to the
 * minute so the last minute still shows "1 min" (same as the PHP partial).
 *
 * @param {number} left Seconds until the target.
 * @return {{days: number, hours: number, minutes: number}} Parts.
 */
function split(left) {
  const total = Math.ceil(left / MINUTE);

  return {
    days: Math.floor(total / DAY),
    hours: Math.floor((total % DAY) / HOUR),
    minutes: total % HOUR,
  };
}

/**
 * Inline format: "D dagar H tim", "H tim M min", "M min".
 *
 * @param {number} left   Seconds until the target.
 * @param {Object} labels Translated unit labels.
 * @return {string} Formatted time left.
 */
export function formatLeft(left, labels) {
  if (left <= 0) {
    return labels.closed;
  }

  const { days, hours, minutes } = split(left);

  if (days) {
    return `${days} ${days === 1 ? labels.day : labels.days} ${hours} ${labels.hour}`;
  }

  if (hours) {
    return `${hours} ${labels.hour} ${minutes} ${labels.minute}`;
  }

  return `${minutes} ${labels.minute}`;
}

function write(element, text) {
  if (element && element.textContent !== text) {
    element.textContent = text;
  }
}

function render(root, left) {
  const labels = JSON.parse(root.dataset.labels);

  if (root.dataset.variant !== 'large') {
    write(root.querySelector('[data-countdown-value]'), formatLeft(left, labels));
    return;
  }

  const parts = split(left);

  Object.keys(parts).forEach((unit) => {
    write(root.querySelector(`[data-countdown-${unit}]`), String(parts[unit]).padStart(2, '0'));
  });
  write(root.querySelector('[data-countdown-days-label]'), parts.days === 1 ? labels.day : labels.days);
}

function tick() {
  const now = Date.now();

  roots.forEach((root) => {
    const end = Date.parse(root.dataset.end);

    if (Number.isNaN(end)) {
      return;
    }

    const left = Math.max(0, Math.round((end - now) / 1000));

    render(root, left);

    // Once per target: the hero and the ballot count down to the same deadline.
    if (!left && !fired.has(end)) {
      fired.add(end);
      document.dispatchEvent(new CustomEvent(END_EVENT, { detail: { end } }));
    }
  });
}

/**
 * Point a countdown at a new target and render it right away. A target that
 * has already passed fires the event at once.
 *
 * @param {HTMLElement} root [data-awards-countdown] element from the page.
 * @param {string}      end  Target as ISO 8601.
 */
export function setCountdownEnd(root, end) {
  root.dataset.end = end;
  tick();
}

export default function initCountdowns() {
  document.querySelectorAll('[data-awards-countdown]').forEach((root) => roots.add(root));

  if (!roots.size) {
    return;
  }

  tick();
  setInterval(tick, 1000);

  // Background tabs run timers as rarely as once a minute; catch up on return.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      tick();
    }
  });
}
